import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../Configurations/Urls";
import {
  FALLBACK_INSTITUTE_TIMINGS,
  formatHourHeader,
  resolveTimelineSlots,
  timeToMinutes,
} from "./schedulingTimeline";
import "./SlotAvailability.css";

const DAYS = [
  { id: "mon", label: "M", name: "Monday" },
  { id: "tue", label: "T", name: "Tuesday" },
  { id: "wed", label: "W", name: "Wednesday" },
  { id: "thu", label: "T", name: "Thursday" },
  { id: "fri", label: "F", name: "Friday" },
  { id: "sat", label: "S", name: "Saturday" },
  { id: "sun", label: "S", name: "Sunday" },
];

const FILTER_CONFIG = [
  { key: "batch", label: "Batch", options: ["All"], optionType: "batch" },
  { key: "course", label: "Course", options: ["All", "AI", "Data Science", "Web", "Cloud"] },
  { key: "mode", label: "Mode", options: ["All", "Online", "Offline", "Hybrid"] },
  { key: "shift", label: "Shift", options: ["All", "Morning", "Afternoon", "Evening"] },
  { key: "trainer", label: "Trainer", options: ["All", "Any", "Rahul", "Priya", "Vikas"] },
  { key: "spaceStatus", label: "Training Space Status", options: ["All", "Active", "Inactive"] },
  { key: "status", label: "Status", options: ["All", "Free", "Partial", "Occupied"] },
];

const STATUS_COLORS = {
  free: "#e8f5e9",
  partial: "#fff8e1",
  occupied: "#f28b82",
};

const DAY_INDEX_TO_ID = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const getCellStatus = (cell) => {
  const isBooked =
    !!cell?.booking || cell?.fill?.top === "booked" || cell?.fill?.bottom === "booked";
  if (isBooked) return "occupied";
  const isAvailable =
    cell?.fill?.top === "available" || cell?.fill?.bottom === "available";
  if (isAvailable) return "free";
  return "partial";
};

const getDayIdFromDate = (dateValue) => {
  const parsed = new Date(`${dateValue}T00:00:00`);
  return DAY_INDEX_TO_ID[parsed.getDay()];
};

const normalizeSlotId = (raw) => {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (digits.length >= 4) return digits.slice(0, 4);
  if (digits.length === 3) return `0${digits}`;
  return digits.padStart(4, "0");
};

const formatHourLabel = (slot) => {
  const source = slot?.start || slot?.id || "";
  const normalized = normalizeSlotId(source);
  const hour24 = Number(normalized.slice(0, 2));
  if (!Number.isFinite(hour24)) return slot?.label || "";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const ampm = hour24 < 12 ? "AM" : "PM";
  return `${hour12} ${ampm}`;
};

const formatTimeAmPm = (value) => {
  if (!value) return "-";
  const raw = String(value).trim();
  const hhmmMatch = raw.match(/^(\d{1,2}):(\d{2})$/);
  const fourDigitMatch = raw.match(/^(\d{1,2})(\d{2})$/);
  let hour = null;
  let minute = null;
  if (hhmmMatch) {
    hour = Number(hhmmMatch[1]);
    minute = Number(hhmmMatch[2]);
  } else if (fourDigitMatch) {
    hour = Number(fourDigitMatch[1]);
    minute = Number(fourDigitMatch[2]);
  }
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return raw;
  const ampm = hour < 12 ? "AM" : "PM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${ampm}`;
};

const formatMinutesToTime = (totalMinutes) => {
  const minutes = Math.max(0, Math.min(23 * 60 + 59, Number(totalMinutes) || 0));
  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const ampm = hour24 < 12 ? "AM" : "PM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${ampm}`;
};

const getHalfHourLabels = (hourKey) => {
  const hour24 = Number(String(hourKey || "").slice(0, 2));
  if (!Number.isFinite(hour24)) return { top: "-", bottom: "-" };
  const start = hour24 * 60;
  const mid = start + 30;
  const end = start + 60;
  return {
    top: `${formatMinutesToTime(start)} - ${formatMinutesToTime(mid)}`,
    bottom: `${formatMinutesToTime(mid)} - ${formatMinutesToTime(end)}`,
  };
};

const hasOverlap = (startA, endA, startB, endB) => {
  if (
    !Number.isFinite(startA) ||
    !Number.isFinite(endA) ||
    !Number.isFinite(startB) ||
    !Number.isFinite(endB)
  ) {
    return false;
  }
  return startA < endB && endA > startB;
};

const resolveSegmentFill = (
  segStart,
  segEnd,
  spaceBookings,
  spaceAvailability,
  instituteStart,
  instituteEnd
) => {
  if (!hasOverlap(segStart, segEnd, instituteStart, instituteEnd)) {
    return "unavailable";
  }

  if (spaceBookings.some((b) => hasOverlap(b.__startMinutes, b.__endMinutes, segStart, segEnd))) {
    return "booked";
  }

  if (spaceAvailability.some((iv) => hasOverlap(iv.start, iv.end, segStart, segEnd))) {
    return "available";
  }

  return "unavailable";
};

const getBatchCodeLabel = (booking) => {
  const directCode =
    booking?.batchCode ||
    booking?.batch_code ||
    booking?.code ||
    "";
  if (String(directCode).trim()) return String(directCode).trim();

  const display = String(booking?.batchDisplay || "").trim();
  if (display) {
    const [firstToken = ""] = display.split(/\s+/);
    if (firstToken) return firstToken;
  }

  const fallback = String(booking?.batchName || booking?.batchId || "-").trim();
  return fallback || "-";
};

const formatBatchDate = (value) => {
  if (!value) return "-";
  const raw = String(value).trim();
  if (!raw) return "-";
  const parsed = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatBatchDateRange = (start, end) =>
  `${formatBatchDate(start)} - ${end ? formatBatchDate(end) : "Open"}`;

const formatBatchLabel = (code, name) => {
  const batchCode = String(code || "").trim();
  const batchName = String(name || "").trim();
  if (!batchCode) return batchName || "-";
  if (!batchName || batchName === batchCode) return batchCode;
  return `${batchCode} — ${batchName}`;
};

const getTrainerLabel = (booking) => {
  const name = String(
    booking?.trainerName || booking?.trainer_name || booking?.trainer || ""
  )
    .replace(/\s+/g, " ")
    .trim();
  return name || "-";
};

const getBookingBarStyleHorizontal = (booking, colStart, colspan) => {
  const spanMinutes = colspan * 60;
  const startOffset = Math.max(0, booking.__startMinutes - colStart);
  const duration = Math.max(0, booking.__endMinutes - booking.__startMinutes);
  const leftPct = (startOffset / spanMinutes) * 100;
  const widthPct = Math.min(100 - leftPct, (duration / spanMinutes) * 100);

  return {
    left: `${leftPct}%`,
    width: `${Math.max(widthPct, 18)}%`,
  };
};

const buildSpaceRowCells = (
  space,
  slots,
  spaceBookings,
  spaceAvailability,
  instituteTimings = FALLBACK_INSTITUTE_TIMINGS
) => {
  const instituteStart =
    timeToMinutes(instituteTimings?.institute_start_time) ??
    timeToMinutes(FALLBACK_INSTITUTE_TIMINGS.institute_start_time);
  const instituteEnd =
    timeToMinutes(instituteTimings?.institute_end_time) ??
    timeToMinutes(FALLBACK_INSTITUTE_TIMINGS.institute_end_time);

  const cells = [];
  let index = 0;

  while (index < slots.length) {
    const slot = slots[index];
    const colStart = Number(slot.key.slice(0, 2)) * 60;
    const colMid = colStart + 30;
    const colEnd = colStart + 60;
    const prevColStart = colStart - 60;
    const booking =
      spaceBookings.find((b) =>
        hasOverlap(b.__startMinutes, b.__endMinutes, colStart, colEnd)
      ) || null;
    const continuedFromPrev =
      booking &&
      index > 0 &&
      hasOverlap(booking.__startMinutes, booking.__endMinutes, prevColStart, colStart);

    if (booking && !continuedFromPrev) {
      let colspan = 1;
      let nextIndex = index + 1;
      while (nextIndex < slots.length) {
        const nextStart = Number(slots[nextIndex].key.slice(0, 2)) * 60;
        const nextEnd = nextStart + 60;
        if (hasOverlap(booking.__startMinutes, booking.__endMinutes, nextStart, nextEnd)) {
          colspan += 1;
          nextIndex += 1;
        } else {
          break;
        }
      }

      const cell = {
        key: `${space.id}-${slot.key}`,
        slot,
        colStart,
        colspan,
        booking,
        fill: {
          top: resolveSegmentFill(
            colStart,
            colMid,
            spaceBookings,
            spaceAvailability,
            instituteStart,
            instituteEnd
          ),
          bottom: resolveSegmentFill(
            colMid,
            colEnd,
            spaceBookings,
            spaceAvailability,
            instituteStart,
            instituteEnd
          ),
        },
        isBookingStart: true,
      };
      cells.push({ ...cell, cellStatus: "occupied" });
      index += colspan;
    } else {
      const cell = {
        key: `${space.id}-${slot.key}`,
        slot,
        colStart,
        colspan: 1,
        booking: continuedFromPrev ? booking : null,
        fill: {
          top: resolveSegmentFill(
            colStart,
            colMid,
            spaceBookings,
            spaceAvailability,
            instituteStart,
            instituteEnd
          ),
          bottom: resolveSegmentFill(
            colMid,
            colEnd,
            spaceBookings,
            spaceAvailability,
            instituteStart,
            instituteEnd
          ),
        },
        isBookingStart: false,
      };
      cells.push({ ...cell, cellStatus: getCellStatus(cell) });
      index += 1;
    }
  }

  return cells;
};

const OccupiedLabelWithTooltip = ({ booking, space, colSpan = 1, className = "" }) => {
  const cellRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [tooltipCoords, setTooltipCoords] = useState(null);

  const batchCode = getBatchCodeLabel(booking);
  const batchLabel = formatBatchLabel(batchCode, booking?.batchName);
  const batchDateRange = formatBatchDateRange(booking.batchStartDate, booking.batchEndDate);
  const trainerLabel = getTrainerLabel(booking);
  const startTime = formatTimeAmPm(
    booking.batchClassStartTime || booking._start || booking.start
  );
  const endTime = formatTimeAmPm(booking.batchClassEndTime || booking._end || booking.end);
  const spaceLabel = `${space?.name || "-"} (Floor ${space?.floor_number ?? "-"})`;

  const updateTooltipPosition = () => {
    const el = cellRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const tooltipHeight = 260;
    const margin = 12;
    const isNarrow = window.innerWidth <= 760;
    const showBelow = rect.bottom + tooltipHeight + margin < window.innerHeight;
    setTooltipCoords({
      left: isNarrow
        ? margin
        : Math.min(
            Math.max(rect.left + rect.width / 2, 160),
            window.innerWidth - 160
          ),
      top: isNarrow
        ? showBelow
          ? rect.bottom + 10
          : Math.max(margin, window.innerHeight - tooltipHeight - margin)
        : showBelow
          ? Math.min(rect.bottom + 10, window.innerHeight - margin)
          : Math.max(rect.top - 10, margin),
      placement: isNarrow || showBelow ? "bottom" : "top",
      isNarrow,
    });
  };

  const showDetails = () => {
    updateTooltipPosition();
    setOpen(true);
  };

  const hideDetails = () => {
    setOpen(false);
    setTooltipCoords(null);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleReposition = () => updateTooltipPosition();
    const handleOutside = (event) => {
      if (cellRef.current && !cellRef.current.contains(event.target)) {
        hideDetails();
      }
    };
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    document.addEventListener("pointerdown", handleOutside);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
      document.removeEventListener("pointerdown", handleOutside);
    };
  }, [open]);

  const tooltipContent =
    open && tooltipCoords
      ? createPortal(
          <div
            className={`trainer-sheet-batch-tooltip trainer-sheet-batch-tooltip--portal trainer-sheet-batch-tooltip--${tooltipCoords.placement}${tooltipCoords.isNarrow ? " trainer-sheet-batch-tooltip--mobile" : ""}`}
            style={{
              left: tooltipCoords.left,
              top: tooltipCoords.top,
              transform: tooltipCoords.isNarrow
                ? "none"
                : tooltipCoords.placement === "bottom"
                  ? "translateX(-50%)"
                  : "translate(-50%, -100%)",
            }}
            role="tooltip"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Batch</span>
              <span className="trainer-sheet-batch-tooltip__value">{batchLabel}</span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Dates</span>
              <span className="trainer-sheet-batch-tooltip__value">{batchDateRange}</span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Trainer</span>
              <span className="trainer-sheet-batch-tooltip__value">{trainerLabel}</span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Course</span>
              <span className="trainer-sheet-batch-tooltip__value">{booking.course || "-"}</span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Class</span>
              <span className="trainer-sheet-batch-tooltip__value">
                {startTime} - {endTime}
              </span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Status</span>
              <span className="trainer-sheet-batch-tooltip__value">
                {booking.classStatus || booking.batchStatus || "Scheduled"}
              </span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Space</span>
              <span className="trainer-sheet-batch-tooltip__value">{spaceLabel}</span>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <td
        ref={cellRef}
        colSpan={colSpan}
        className={`timeline-cell timeline-cell--occupied-label ${className}`.trim()}
        title="Tap or hover for details"
        onMouseEnter={showDetails}
        onMouseLeave={hideDetails}
        onClick={(e) => {
          e.stopPropagation();
          if (open) hideDetails();
          else showDetails();
        }}
      >
        <span className="slot-occupied-text">{batchCode}</span>
      </td>
      {tooltipContent}
    </>
  );
};

const SlotTimelineBookingCard = ({ booking, space, colStart, colspan }) => {
  const cardRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [tooltipCoords, setTooltipCoords] = useState(null);
  const barStyle = getBookingBarStyleHorizontal(booking, colStart, colspan);

  const batchCode = getBatchCodeLabel(booking);
  const batchLabel = formatBatchLabel(batchCode, booking?.batchName);
  const batchDateRange = formatBatchDateRange(booking.batchStartDate, booking.batchEndDate);
  const trainerLabel = getTrainerLabel(booking);
  const startTime = formatTimeAmPm(
    booking.batchClassStartTime || booking._start || booking.start
  );
  const endTime = formatTimeAmPm(booking.batchClassEndTime || booking._end || booking.end);
  const spaceLabel = `${space?.name || "-"} (Floor ${space?.floor_number ?? "-"})`;

  const updateTooltipPosition = () => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const tooltipHeight = 250;
    const showBelow = rect.bottom + tooltipHeight + 16 < window.innerHeight;
    setTooltipCoords({
      left: rect.left + rect.width / 2,
      top: showBelow ? rect.bottom + 10 : rect.top - 10,
      placement: showBelow ? "bottom" : "top",
    });
  };

  useEffect(() => {
    if (!hovered) return undefined;
    const handleReposition = () => updateTooltipPosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [hovered]);

  const tooltipContent =
    hovered && tooltipCoords
      ? createPortal(
          <div
            className={`trainer-sheet-batch-tooltip trainer-sheet-batch-tooltip--portal trainer-sheet-batch-tooltip--${tooltipCoords.placement}`}
            style={{
              left: tooltipCoords.left,
              top: tooltipCoords.top,
              transform:
                tooltipCoords.placement === "bottom"
                  ? "translateX(-50%)"
                  : "translate(-50%, -100%)",
            }}
            role="tooltip"
          >
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Batch</span>
              <span className="trainer-sheet-batch-tooltip__value">{batchLabel}</span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Dates</span>
              <span className="trainer-sheet-batch-tooltip__value">{batchDateRange}</span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Trainer</span>
              <span className="trainer-sheet-batch-tooltip__value">{trainerLabel}</span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Course</span>
              <span className="trainer-sheet-batch-tooltip__value">{booking.course || "-"}</span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Class</span>
              <span className="trainer-sheet-batch-tooltip__value">
                {startTime} - {endTime}
              </span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Status</span>
              <span className="trainer-sheet-batch-tooltip__value">
                {booking.classStatus || booking.batchStatus || "Scheduled"}
              </span>
            </div>
            <div className="trainer-sheet-batch-tooltip__row">
              <span className="trainer-sheet-batch-tooltip__label">Space</span>
              <span className="trainer-sheet-batch-tooltip__value">{spaceLabel}</span>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div className="slot-sheet-booking-track">
        <div
          ref={cardRef}
          className="slot-sheet-batch-wrap"
          style={barStyle}
          onMouseEnter={() => {
            updateTooltipPosition();
            setHovered(true);
          }}
          onMouseLeave={() => {
            setHovered(false);
            setTooltipCoords(null);
          }}
        >
          <div className="slot-sheet-batch">
            <strong title={batchCode}>{batchCode}</strong>
          </div>
        </div>
      </div>
      {tooltipContent}
    </>
  );
};

const SlotAvailability = () => {
  const { userData } = useAuthStore();
  const token = localStorage.getItem("token");
  const organizationId =
    userData?.organization?.organization_id ||
    userData?.activeOrganizationEntity?.organization_id ||
    userData?.organization_id;
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [filters, setFilters] = useState({
    batch: "All",
    course: "All",
    mode: "All",
    shift: "All",
    trainer: "All",
    spaceStatus: "Active",
    status: "All",
  });
  const [spaces, setSpaces] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [batches, setBatches] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [instituteTimings, setInstituteTimings] = useState(FALLBACK_INSTITUTE_TIMINGS);
  const [apiTimelineSlots, setApiTimelineSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoverTimeState, setHoverTimeState] = useState(null);
  const rootRef = useRef(null);
  const [isCompactView, setIsCompactView] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth > 760
  );
  const [isMobileView, setIsMobileView] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth <= 760
  );

  const sendPrompt = async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const selectedDayId = getDayIdFromDate(selectedDate);
      const payload = {
        organization_id: organizationId,
        date: selectedDate,
        dayId: selectedDayId,
        activeDays: [selectedDayId],
        ...filters,
      };
      const response = await axios.post(`${MAIN_URL}/api/ems/batches/schedule-board`, payload, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = response?.data || {};
      setSpaces(Array.isArray(data.spaces) ? data.spaces : []);
      setBookings(Array.isArray(data.bookings) ? data.bookings : []);
      setBatches(
        Array.isArray(data.batches)
          ? data.batches
          : Array.isArray(data.filterOptions?.batches)
            ? data.filterOptions.batches
            : []
      );
      setFilterOptions(data.filterOptions || {});
      setInstituteTimings(data.instituteTimings || FALLBACK_INSTITUTE_TIMINGS);
      setApiTimelineSlots(Array.isArray(data.timelineSlots) ? data.timelineSlots : []);
    } catch (error) {
      console.error("Failed to fetch schedule board", error);
      setSpaces([]);
      setBookings([]);
      setInstituteTimings(FALLBACK_INSTITUTE_TIMINGS);
      setApiTimelineSlots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    sendPrompt();
  }, [
    organizationId,
    selectedDate,
    filters.batch,
    filters.course,
    filters.mode,
    filters.shift,
    filters.trainer,
    filters.spaceStatus,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;

    const updateMode = () => {
      const width = node.getBoundingClientRect().width;
      setIsCompactView(width < 1450);
      const mobile = width <= 760;
      setIsMobileView(mobile);
      setFiltersExpanded((prev) => (mobile ? prev : true));
    };

    updateMode();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => updateMode());
      observer.observe(node);
    } else {
      window.addEventListener("resize", updateMode);
    }

    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener("resize", updateMode);
    };
  }, []);

  const spacesForTable = useMemo(() => {
    return spaces
      .filter((space) => {
        if (filters.spaceStatus === "All") return true;
        return String(space?.status || "").toLowerCase() === filters.spaceStatus.toLowerCase();
      })
      .map((space) => ({
        id: String(space.id),
        name: space.name || "-",
        floor_number:
          space?.floor_number === null || space?.floor_number === undefined
            ? "-"
            : String(space.floor_number),
        time_slots: Array.isArray(space?.time_slots) ? space.time_slots : [],
      }));
  }, [filters.spaceStatus, spaces]);

  const slotsForTable = useMemo(() => {
    return resolveTimelineSlots({
      timelineSlots: apiTimelineSlots,
      instituteTimings,
    }).map((slot) => ({
      key: slot.key,
      label: slot.label || formatHourLabel({ key: slot.key }),
    }));
  }, [apiTimelineSlots, instituteTimings]);

  const bookingsBySpaceAndStartHour = useMemo(() => {
    const map = new Map();
    bookings.forEach((booking) => {
      const slotId = normalizeSlotId(booking.slotId || "");
      const startMinutes =
        timeToMinutes(booking.batchClassStartTime) ??
        timeToMinutes(booking._start) ??
        timeToMinutes(booking.start) ??
        timeToMinutes(slotId);
      const endMinutesRaw =
        timeToMinutes(booking.batchClassEndTime) ??
        timeToMinutes(booking._end) ??
        timeToMinutes(booking.end);
      const endMinutes =
        endMinutesRaw && startMinutes !== null && endMinutesRaw > startMinutes
          ? endMinutesRaw
          : startMinutes !== null
            ? startMinutes + 60
            : null;
      const spaceId = String(booking.spaceId || "");
      if (!slotId || !spaceId || startMinutes === null || endMinutes === null) return;
      const startHourKey = `${String(Math.floor(startMinutes / 60)).padStart(2, "0")}00`;
      const key = `${startHourKey}::${spaceId}`;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push({ ...booking, __startMinutes: startMinutes, __endMinutes: endMinutes });
    });
    map.forEach((arr) => arr.sort((a, b) => a.__startMinutes - b.__startMinutes));
    return map;
  }, [bookings]);

  const bookingsBySpace = useMemo(() => {
    const map = new Map();
    bookingsBySpaceAndStartHour.forEach((arr) => {
      arr.forEach((b) => {
        const spaceId = String(b.spaceId || "");
        if (!spaceId) return;
        if (!map.has(spaceId)) map.set(spaceId, []);
        map.get(spaceId).push(b);
      });
    });
    map.forEach((arr) => arr.sort((a, b) => a.__startMinutes - b.__startMinutes));
    return map;
  }, [bookingsBySpaceAndStartHour]);

  const spaceAvailabilityById = useMemo(() => {
    const map = new Map();
    spacesForTable.forEach((space) => {
      const intervals = (Array.isArray(space.time_slots) ? space.time_slots : [])
        .map((slot) => {
          const start = timeToMinutes(slot?.start_time);
          const end = timeToMinutes(slot?.end_time);
          if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
          return { start, end };
        })
        .filter(Boolean);
      map.set(space.id, intervals);
    });
    return map;
  }, [spacesForTable]);

  const statusFilter =
    filters.status === "All" ? null : String(filters.status).trim().toLowerCase();

  const allSpaceRows = useMemo(() => {
    return spacesForTable.map((space) => {
      const spaceBookings = bookingsBySpace.get(space.id) || [];
      const spaceAvailability = spaceAvailabilityById.get(space.id) || [];
      const cells = buildSpaceRowCells(
        space,
        slotsForTable,
        spaceBookings,
        spaceAvailability,
        instituteTimings
      );
      return { space, cells };
    });
  }, [bookingsBySpace, spaceAvailabilityById, slotsForTable, spacesForTable, instituteTimings]);

  const spaceRows = useMemo(() => {
    if (!statusFilter) return allSpaceRows;

    return allSpaceRows
      .filter((row) => row.cells.some((cell) => cell.cellStatus === statusFilter))
      .map((row) => ({
        ...row,
        cells: row.cells.filter((cell) => cell.cellStatus === statusFilter),
      }));
  }, [allSpaceRows, statusFilter]);

  const visibleSlotsForGrid = useMemo(() => {
    if (!statusFilter) return slotsForTable;

    const visibleKeys = new Set();
    spaceRows.forEach((row) => {
      row.cells.forEach((cell) => {
        if (cell.slot?.key) visibleKeys.add(cell.slot.key);
      });
    });

    return slotsForTable.filter((slot) => visibleKeys.has(slot.key));
  }, [spaceRows, slotsForTable, statusFilter]);

  const handleTimelineMouseMove = (event, colHour, spaceId) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    const minuteOffset = Math.min(55, Math.max(0, Math.round((ratio * 60) / 5) * 5));
    const totalMinutes = colHour * 60 + minuteOffset;
    setHoverTimeState({
      key: `${colHour}-${spaceId}`,
      value: formatMinutesToTime(totalMinutes),
      left: `${Math.max(4, Math.min(rect.width - 22, x))}px`,
    });
  };

  const filterConfigWithApiOptions = useMemo(() => {
    return FILTER_CONFIG.map((item) => {
      if (item.key === "batch") {
        const batchOptions = Array.isArray(filterOptions.batch) ? filterOptions.batch : [];
        if (batchOptions.length) {
          return { ...item, options: batchOptions, optionType: "batch" };
        }
        const fallback = (Array.isArray(batches) ? batches : []).map((batch) => ({
          id: String(batch.batchId || ""),
          label: `${batch.batchCode || batch.batchName || "-"} (${formatBatchDateRange(
            batch.batchStartDate,
            batch.batchEndDate
          )})`,
        }));
        return {
          ...item,
          options: [{ id: "All", label: "All" }, ...fallback.filter((opt) => opt.id)],
          optionType: "batch",
        };
      }

      return {
        ...item,
        options:
          Array.isArray(filterOptions[item.key]) && filterOptions[item.key].length
            ? filterOptions[item.key]
            : item.options,
      };
    });
  }, [filterOptions, batches]);

  return (
    <section
      ref={rootRef}
      className={`slot-availability slot-availability--transposed ${isCompactView ? "slot-availability--compact" : "slot-availability--expanded"}`}
    >
      <header className="slot-availability__header">
        <h2 className="slot-availability__title">Slot availability</h2>
        <div className="slot-availability__legend slot-availability-legend">
          <span className="slot-availability__legend-item">
            <i className="slot-availability-legend-swatch slot-availability-legend-swatch--available" />
            Available
          </span>
          <span className="slot-availability__legend-item">
            <i className="slot-availability-legend-swatch slot-availability-legend-swatch--unavailable" />
            Not Available
          </span>
          <span className="slot-availability__legend-item">
            <i className="slot-availability-legend-swatch slot-availability-legend-swatch--occupied" />
            Class Running
          </span>
        </div>
      </header>

      <div className="slot-availability__controls">
        <div className="slot-availability__date-row">
          <label className="date-chip">
            <span>Date</span>
            <input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="slot-filters-toggle"
            aria-expanded={filtersExpanded}
            onClick={() => setFiltersExpanded((open) => !open)}
          >
            Filters
            <span aria-hidden="true">{filtersExpanded ? "▴" : "▾"}</span>
          </button>
        </div>

        <div
          className={`slot-availability__filters ${
            filtersExpanded ? "is-open" : "is-collapsed"
          }`}
        >
          {filterConfigWithApiOptions.map((filter) => (
            <label className="slot-select" key={filter.key}>
              <span>{filter.label}</span>
              <select
                value={filters[filter.key]}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, [filter.key]: event.target.value }))
                }
              >
                {filter.optionType === "batch"
                  ? filter.options.map((option) => {
                      const value = typeof option === "object" ? option.id : option;
                      const label = typeof option === "object" ? option.label : option;
                      return (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      );
                    })
                  : filter.options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
              </select>
            </label>
          ))}
        </div>

        <div className="slot-availability__days-row">
          <button type="button" className="search-button" onClick={sendPrompt}>
            {loading ? "Loading..." : "Search availability"}
          </button>
        </div>
      </div>

      <div className="slot-availability__table-wrap">
        <table className="slot-grid">
          <colgroup>
            <col className="slot-grid-col-space" />
            {visibleSlotsForGrid.map((slot) => (
              <col key={slot.key} className="slot-grid-col-time" />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="slot-grid-corner">Space</th>
              {visibleSlotsForGrid.map((slot) => (
                <th key={slot.key} className="slot-grid-time-head">
                  {formatHourHeader(slot.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {spaceRows.map(({ space }) => {
              const fullRow = allSpaceRows.find((row) => row.space.id === space.id);
              const cells = fullRow?.cells || [];
              const rowCells = [];
              let slotIndex = 0;

              while (slotIndex < visibleSlotsForGrid.length) {
                const slot = visibleSlotsForGrid[slotIndex];
                const cell = cells.find((item) => item.slot.key === slot.key);
                if (!cell) {
                  rowCells.push(
                    <td
                      key={`${space.id}-${slot.key}-fallback`}
                      className="timeline-cell timeline-cell--unavailable timeline-cell-bottom--unavailable"
                    >
                      <div className="slot-empty slot-empty--timeline" />
                    </td>
                  );
                  slotIndex += 1;
                  continue;
                }

                const matchesStatus = !statusFilter || cell.cellStatus === statusFilter;

                if (cell.isBookingStart && cell.booking && matchesStatus) {
                  let colspan = 1;
                  for (let nextIndex = slotIndex + 1; nextIndex < visibleSlotsForGrid.length; nextIndex += 1) {
                    const nextSlot = visibleSlotsForGrid[nextIndex];
                    const nextStart = Number(nextSlot.key.slice(0, 2)) * 60;
                    const nextEnd = nextStart + 60;
                    if (
                      hasOverlap(
                        cell.booking.__startMinutes,
                        cell.booking.__endMinutes,
                        nextStart,
                        nextEnd
                      )
                    ) {
                      colspan += 1;
                    } else {
                      break;
                    }
                  }
                  if (isMobileView) {
                    rowCells.push(
                      <OccupiedLabelWithTooltip
                        key={`${space.id}-${slot.key}-occupied`}
                        booking={cell.booking}
                        space={space}
                        colSpan={colspan}
                      />
                    );
                    slotIndex += colspan;
                    continue;
                  }
                  const colStart = Number(slot.key.slice(0, 2)) * 60;
                  rowCells.push(
                    <td
                      key={`${space.id}-${slot.key}`}
                      colSpan={colspan}
                      className="timeline-cell timeline-cell--booked-span"
                    >
                      <SlotTimelineBookingCard
                        booking={cell.booking}
                        space={space}
                        colStart={colStart}
                        colspan={colspan}
                      />
                    </td>
                  );
                  slotIndex += colspan;
                  continue;
                }

                if (!matchesStatus) {
                  rowCells.push(
                    <td
                      key={`${space.id}-${slot.key}-hidden`}
                      className="timeline-cell timeline-cell--hidden"
                      aria-hidden="true"
                    />
                  );
                  slotIndex += 1;
                  continue;
                }

                const colHour = Number(slot.key.slice(0, 2));
                rowCells.push(
                  <td
                    key={cell.key}
                    className={`timeline-cell timeline-cell--${cell.fill.top} timeline-cell-bottom--${cell.fill.bottom}`}
                    onMouseMove={(event) => handleTimelineMouseMove(event, colHour, space.id)}
                    onMouseLeave={() => setHoverTimeState(null)}
                  >
                    <div className="slot-empty slot-empty--timeline" />
                    {hoverTimeState?.key === `${colHour}-${space.id}` ? (
                      <span
                        className="timeline-hover-time timeline-hover-time--horizontal"
                        style={{ left: hoverTimeState.left }}
                      >
                        {hoverTimeState.value}
                      </span>
                    ) : null}
                  </td>
                );
                slotIndex += 1;
              }

              return (
                <tr key={space.id}>
                  <th scope="row" className="slot-space-row-head">
                    <div className="slot-space-head">
                      <span className="slot-space-head__name">{space.name}</span>
                      <span className="slot-space-head__floor">Floor: {space.floor_number}</span>
                    </div>
                  </th>
                  {rowCells}
                </tr>
              );
            })}
            {!spaceRows.length ? (
              <tr>
                <td colSpan={Math.max(2, visibleSlotsForGrid.length + 1)} className="slot-time">
                  No availability data for selected filters/date.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <p className="slot-availability__scroll-hint">Swipe sideways to see more hours →</p>
    </section>
  );
};

export default SlotAvailability;
